import resolveEnv, { PROD, resolveDomain, TITLESOLUTIONS } from '@fluentsms/utils';
import { Auth0DecodedHash } from 'auth0-js';
import jwtDecode, { JwtPayload } from 'jwt-decode';

export type FluentJwtPayload = JwtPayload & {
  [domainUrl: string]: {
    tenantId: string;
    userId: string;
    displayName: string;
    // Deprecated use securityV2 instead.
    security?: string;
    securityV2: string;
    securityVersion: string;
  };
} & {
  nickname: string;
  name: string;
  picture: string;
  updated_at: string;
  email: string;
  email_verified: string;
};

export class TokenUtility {
  private static readonly accessTokenKey = 'fluentsms.access.tkn';
  private static readonly idTokenKey = 'fluentsms.id.tkn';
  private static readonly idPayloadKey = 'fluentsms.id.payload';
  private static readonly mfaTokenKey = 'fluentsms.mfa.tkn';

  public setTokens(authResult: Auth0DecodedHash | null): void {
    console.log('Setting tokens...');
    if (!authResult) {
      return;
    }

    const { idToken, idTokenPayload, accessToken } = authResult;

    localStorage.setItem(TokenUtility.idTokenKey, idToken as string);
    localStorage.setItem(TokenUtility.accessTokenKey, accessToken as string);

    if (idTokenPayload) localStorage.setItem(TokenUtility.idPayloadKey, JSON.stringify(idTokenPayload));

    console.log('Tokens set.');
  }

  public setMfaToken(authResult: Auth0DecodedHash | null): void {
    const mfaToken = authResult?.accessToken;
    if (!mfaToken) return;

    localStorage.setItem(TokenUtility.mfaTokenKey, mfaToken);
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(TokenUtility.accessTokenKey);
  }

  public getIdToken(): string | null {
    return localStorage.getItem(TokenUtility.idTokenKey);
  }

  public getMfaToken(): string | null {
    return localStorage.getItem(TokenUtility.mfaTokenKey);
  }

  public clearStorage(): void {
    localStorage.removeItem(TokenUtility.idTokenKey);
    localStorage.removeItem(TokenUtility.accessTokenKey);
    localStorage.removeItem(TokenUtility.mfaTokenKey);
    sessionStorage.clear();
  }

  /**
   * Used internally for getDecodedToken
   * Can also be used for getCompleteDecodedToken's return object
   */
  public getUserTokenDomain() {
    const env = resolveEnv() === PROD ? '' : `${resolveEnv().toLowerCase()}.`;
    const domain =
      resolveDomain() === TITLESOLUTIONS ? `fluentapi.${resolveDomain()}.com` : `api.${resolveDomain()}.com`;

    return `https://${env}${domain}/users/auth`;
  }

  public getDecodedDetailToken() {
    const decodedToken = this.getCompleteDecodedToken();
    return decodedToken[this.getUserTokenDomain()];
  }

  // This returns the entire token not just inside the environment scope.
  public getCompleteDecodedToken(): FluentJwtPayload {
    return this.extractedToken();
  }

  private extractedToken(): FluentJwtPayload {
    const token = tokenUtility.getIdToken();

    const decodedToken: FluentJwtPayload = jwtDecode(token ?? '');
    return decodedToken;
  }

  public validateToken(token: string | null) {
    try {
      if (!token) {
        return false;
      }

      const decodedToken: JwtPayload = jwtDecode(token);
      const expireDate = (decodedToken?.exp || 0) * 1000;

      if (Date.now() >= expireDate) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get the minimum expiration time between the idToken and accessToken.
   */
  public getMinimumExpiration() {
    const idToken = this.getIdToken();
    const accessToken = this.getAccessToken();

    const expirations = [idToken, accessToken]
      .map((token) => jwtDecode<JwtPayload>(token ?? ''))
      .map(({ exp }) => exp ?? 0);

    return Math.min(...expirations);
  }

  /**
   * Get the maximum issuedAt time between the idToken and accessToken.
   */
  public getMaximumIssuedAt() {
    const idToken = this.getIdToken();
    const accessToken = this.getAccessToken();

    const issuedAts = [idToken, accessToken]
      .map((token) => jwtDecode<JwtPayload>(token ?? ''))
      .map(({ iat }) => iat ?? 0);

    return Math.max(...issuedAts);
  }
}

export const tokenUtility = new TokenUtility();

export default tokenUtility;
